/**
 * DATE AND DATETIME UTILITIES
 * 
 * RULES:
 * ======
 * 1. DATE-ONLY STRINGS (YYYY-MM-DD):
 *    - Display: Reformatted via pure string manipulation (NO Date object conversion)
 *    - Input: Send as-is or append "T00:00:00.000Z" if backend expects datetime
 *    - Example: "2000-06-22" → "Jun 22, 2000" (no timezone bugs!)
 * 
 * 2. DATETIME STRINGS (ISO with 'T'):
 *    - Display: Converted to LOCAL timezone
 *    - Input: Send as UTC (user enters local time → converted to UTC)
 *    - Example: "2026-02-04T12:20:15Z" → displayed in user's local timezone
 * 
 * 3. DATE OBJECTS:
 *    - Only used in datepicker components for user input
 *    - Converted to local timezone for display
 *    - Converted to "YYYY-MM-DD" string via formatDateForBackend()
 * 
 * IMPLEMENTATION NOTES:
 * ====================
 * - formatDate() detects string type by checking for 'T' character
 * - Date-only strings (no 'T') → formatDateOnlyString() → pure string manipulation
 * - Datetime strings (has 'T') → parseISO() + formatInTimeZone() → timezone conversion
 * - Date objects → formatInTimeZone() → timezone conversion
 * 
 * COMMON PATTERNS:
 * ================
 * 
 * DISPLAY DATES FROM BACKEND:
 * ```typescript
 * // Date-only string from backend
 * formatDate("2000-06-22", "MMM dd, yyyy") // → "Jun 22, 2000" (no conversion!)
 * 
 * // Datetime string from backend
 * formatDate("2026-02-04T12:20:15Z", "MMM dd, yyyy HH:mm") // → "Feb 04, 2026 07:20" (if local is EST)
 * ```
 * 
 * FORM DATES → BACKEND:
 * ```typescript
 * // When backend expects datetime, append T00:00:00.000Z
 * const dueDateISO = dateString ? `${dateString}T00:00:00.000Z` : null;
 * // "2025-11-25" → "2025-11-25T00:00:00.000Z"
 * ```
 * 
 * TODO: FUTURE ENHANCEMENT - TIMEZONE SELECTOR
 * =============================================
 * To implement timezone selection feature:
 * 
 * 1. Add configurable timezone variable and replace:
 *    `Intl.DateTimeFormat().resolvedOptions().timeZone`
 *    with `userTimezone`
 * 
 * 2. Create timezone selector UI component in user settings
 * 
 * That's it! All files will automatically use the selected timezone.
 */

import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

/**
 * Format date-only strings (YYYY-MM-DD) without Date object conversion.
 * Avoids timezone bugs by using pure string manipulation.
 */
function formatDateOnlyString(dateString: string, formatString: string): string {
    // Parse YYYY-MM-DD
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
    if (!match) {
        // Return as-is if invalid format
        return dateString;
    }
    
    const [, year, month, day] = match;
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthNamesLong = ['January', 'February', 'March', 'April', 'May', 'June',
                            'July', 'August', 'September', 'October', 'November', 'December'];
    
    // Replace format tokens (order matters - replace longer tokens first!)
    let result = formatString;
    result = result.replace('MMMM', monthNamesLong[monthNum - 1]);
    result = result.replace('MMM', monthNames[monthNum - 1]);
    result = result.replace('MM', month);
    result = result.replace('dd', day);
    result = result.replace(/\bd\b/g, String(dayNum)); // Match 'd' as whole word
    result = result.replace('yyyy', year);
    
    return result;
}

/**
 * Format date for DISPLAY.
 * - Date-only strings (YYYY-MM-DD): Formatted via string manipulation (no Date object conversion)
 * - Datetime strings (with 'T'): Converted to LOCAL timezone
 * - Date objects: Converted to LOCAL timezone
 */
export function formatDate(
    date: Date | string | null | undefined,
    formatString: string
): string {
    if (!date) return '–';
    
    try {
        // STRING INPUT
        if (typeof date === 'string') {
            // Detect datetime vs date-only by checking for 'T'
            if (date.includes('T')) {
                // DATETIME: Convert to local timezone
                const dateObj = parseISO(date);
                const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                return formatInTimeZone(dateObj, timezone, formatString);
            } else {
                // DATE-ONLY: Pure string manipulation (NO Date object conversion)
                return formatDateOnlyString(date, formatString);
            }
        }
        
        // DATE OBJECT: Convert to local timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return formatInTimeZone(date, timezone, formatString);
        
    } catch (error) {
        console.error('Error formatting date:', date, error);
        return '–';
    }
}

/**
 * Format date for BACKEND (from date picker).
 * User selects Nov 25 → backend gets "2025-11-25" (no timezone conversion)
 */
export function formatDateForBackend(date: Date | null | undefined): string | null {
    if (!date) return null;
    
    try {
        // Use LOCAL date components (what user sees in calendar)
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        
        return `${year}-${month}-${day}`;
    } catch (error) {
        console.error('Error formatting date for backend:', date, error);
        return null;
    }
}

/**
 * Format datetime for BACKEND (from datetime picker or filters).
 * User enters local time → backend gets UTC timestamp
 */
export function formatDateTimeForBackend(date: Date | null | undefined): string | null {
    if (!date) return null;
    
    try {
        // .toISOString() automatically converts local time to UTC
        return date.toISOString();
    } catch (error) {
        console.error('Error formatting datetime for backend:', date, error);
        return null;
    }
}
